/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU128Decoder,
  getU128Encoder,
  getU16Decoder,
  getU16Encoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getRecipientDecoder,
  getRecipientEncoder,
  type Recipient,
  type RecipientArgs,
} from '../types';

export const CPMM_MIGRATOR_STATE_DISCRIMINATOR = new Uint8Array([
  169, 86, 255, 187, 37, 248, 11, 176,
]);

export function getCpmmMigratorStateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CPMM_MIGRATOR_STATE_DISCRIMINATOR,
  );
}

export type CpmmMigratorState = {
  discriminator: ReadonlyUint8Array;
  /** The launch this state is associated with */
  launch: Address;
  /** Admin who registered this launch (receives unsold curve base tokens) */
  admin: Address;
  /** CPMM config to use for pool initialization */
  cpmmConfig: Address;
  /** Initial swap fee for the CPMM pool (basis points) */
  initialSwapFeeBps: number;
  /** Initial fee split for the CPMM pool (basis points) */
  initialFeeSplitBps: number;
  /** Padding for alignment */
  pad0: ReadonlyUint8Array;
  /** Hook program to set on the migrated CPMM pool */
  migratedPoolHookProgram: Address;
  /** Hook flags to set on the migrated CPMM pool */
  migratedPoolHookFlags: number;
  /** Recipients for base token distribution at migration */
  recipients: Array<Recipient>;
  /** Minimum quote tokens required to migrate */
  minRaiseQuote: bigint;
  /** Optional minimum price floor (Q64 fixed point) */
  minMigrationPriceQ64Opt: Option<bigint>;
  /** Whether this launch has been migrated */
  isMigrated: boolean;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

export type CpmmMigratorStateArgs = {
  /** The launch this state is associated with */
  launch: Address;
  /** Admin who registered this launch (receives unsold curve base tokens) */
  admin: Address;
  /** CPMM config to use for pool initialization */
  cpmmConfig: Address;
  /** Initial swap fee for the CPMM pool (basis points) */
  initialSwapFeeBps: number;
  /** Initial fee split for the CPMM pool (basis points) */
  initialFeeSplitBps: number;
  /** Padding for alignment */
  pad0: ReadonlyUint8Array;
  /** Hook program to set on the migrated CPMM pool */
  migratedPoolHookProgram: Address;
  /** Hook flags to set on the migrated CPMM pool */
  migratedPoolHookFlags: number;
  /** Recipients for base token distribution at migration */
  recipients: Array<RecipientArgs>;
  /** Minimum quote tokens required to migrate */
  minRaiseQuote: number | bigint;
  /** Optional minimum price floor (Q64 fixed point) */
  minMigrationPriceQ64Opt: OptionOrNullable<number | bigint>;
  /** Whether this launch has been migrated */
  isMigrated: boolean;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link CpmmMigratorStateArgs} account data. */
export function getCpmmMigratorStateEncoder(): Encoder<CpmmMigratorStateArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['launch', getAddressEncoder()],
      ['admin', getAddressEncoder()],
      ['cpmmConfig', getAddressEncoder()],
      ['initialSwapFeeBps', getU16Encoder()],
      ['initialFeeSplitBps', getU16Encoder()],
      ['pad0', fixEncoderSize(getBytesEncoder(), 4)],
      ['migratedPoolHookProgram', getAddressEncoder()],
      ['migratedPoolHookFlags', getU32Encoder()],
      ['recipients', getArrayEncoder(getRecipientEncoder(), { size: 2 })],
      ['minRaiseQuote', getU64Encoder()],
      ['minMigrationPriceQ64Opt', getOptionEncoder(getU128Encoder())],
      ['isMigrated', getBooleanEncoder()],
      ['bump', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 14)],
    ]),
    (value) => ({ ...value, discriminator: CPMM_MIGRATOR_STATE_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link CpmmMigratorState} account data. */
export function getCpmmMigratorStateDecoder(): Decoder<CpmmMigratorState> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['launch', getAddressDecoder()],
    ['admin', getAddressDecoder()],
    ['cpmmConfig', getAddressDecoder()],
    ['initialSwapFeeBps', getU16Decoder()],
    ['initialFeeSplitBps', getU16Decoder()],
    ['pad0', fixDecoderSize(getBytesDecoder(), 4)],
    ['migratedPoolHookProgram', getAddressDecoder()],
    ['migratedPoolHookFlags', getU32Decoder()],
    ['recipients', getArrayDecoder(getRecipientDecoder(), { size: 2 })],
    ['minRaiseQuote', getU64Decoder()],
    ['minMigrationPriceQ64Opt', getOptionDecoder(getU128Decoder())],
    ['isMigrated', getBooleanDecoder()],
    ['bump', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 14)],
  ]);
}

/** Gets the codec for {@link CpmmMigratorState} account data. */
export function getCpmmMigratorStateCodec(): Codec<
  CpmmMigratorStateArgs,
  CpmmMigratorState
> {
  return combineCodec(
    getCpmmMigratorStateEncoder(),
    getCpmmMigratorStateDecoder(),
  );
}

export function decodeCpmmMigratorState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<CpmmMigratorState, TAddress>;
export function decodeCpmmMigratorState<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<CpmmMigratorState, TAddress>;
export function decodeCpmmMigratorState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
):
  | Account<CpmmMigratorState, TAddress>
  | MaybeAccount<CpmmMigratorState, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getCpmmMigratorStateDecoder(),
  );
}

export async function fetchCpmmMigratorState<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<CpmmMigratorState, TAddress>> {
  const maybeAccount = await fetchMaybeCpmmMigratorState(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeCpmmMigratorState<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<CpmmMigratorState, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeCpmmMigratorState(maybeAccount);
}

export async function fetchAllCpmmMigratorState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<CpmmMigratorState>[]> {
  const maybeAccounts = await fetchAllMaybeCpmmMigratorState(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeCpmmMigratorState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<CpmmMigratorState>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeCpmmMigratorState(maybeAccount),
  );
}
